import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RevenueIcon } from '../../../assets/icons/sidebar/revenue-sharing-icon.svg';
import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { StationRevenueSharingInterFace } from '../../../interface/revenue-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  disableStationRevenueSharingApplied,
  fetchStationRevenueSharingFilters,
  fetchStationRevenueSharingList,
  updateStationRevenueSharingFilters,
  setSortData,
} from '../../../redux/slices/StationRevenueSharingSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { getDaysBetween } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import revenueSharingApi from '../../../utils/api/revenue';

import RevenueManagementToggle from './RevenueManageToggle';

interface StationRevenueSharingProperties {
  currentPage: string;
  setCurrentPage: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * User Management page function component
 */
function StationRevenueSharing(properties: Readonly<StationRevenueSharingProperties>) {
  const branding = useAppBranding();
  const { currentPage, setCurrentPage } = properties;
  const { t } = useTranslation('revenue');
  const dispatch = useAppDispatch();
  const {
    revenueList,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.stationRevenueSharing);

  const [modalView, setModalView] = useState({
    status: false,
    title: t('addAccount'),
    header: true,
    content: <div />,
    rightCorner: '',
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Fetch Users list function
   */
  const fetchRevenueSharing = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitError'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    if (!loading) {
      dispatch(fetchStationRevenueSharingList(data));
    }
  };
  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchRevenueSharing({ ...queryParameters, ...value, sortField, sortBy });
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'date' as keyof StationRevenueSharingInterFace,
      sortAllow: true,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStationName' as keyof StationRevenueSharingInterFace,
    },
    {
      label: t('stationId'),
      accessorKey: 'groupBy' as keyof StationRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: `${t('totalAmount')} ${branding.text.currency}`,
      accessorKey: 'totalRevenue' as keyof StationRevenueSharingInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param row
       * @returns
       */
      customData: (row: StationRevenueSharingInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.totalRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('totalPowerConsumed'),
      accessorKey: 'totalMeterDifference' as keyof StationRevenueSharingInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param row
       * @returns
       */
      customData: (row: StationRevenueSharingInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.totalMeterDifference, 3)}</p>
          <span>kWh</span>
        </div>
      ),
    },
    {
      label: t('facilityOwnerRevenue'),
      accessorKey: 'totalFoRevenue' as keyof StationRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Custom data function
       * @param row
       * @returns
       */
      customData: (row: StationRevenueSharingInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.totalFoRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    // {
    //   label: 'Distributor',
    //   accessorKey: 'distributor' as keyof StationRevenueSharingInterFace,
    //   sortAllow: true,
    // },
    {
      label: t('superAdminRevenue'),
      accessorKey: 'totalAdminRevenue' as keyof StationRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Custom data function
       * @param row
       * @returns
       */
      customData: (row: StationRevenueSharingInterFace) => (
        <span>{row?.totalAdminRevenue ? ToFixedValue(row?.totalAdminRevenue) : '0.00'}</span>
      ),
    },
  ];
  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
      groupBy: 'chargingStation',
    };
    if (sortField && sortBy) {
      payload.sortField = sortField;
      payload.sortBy = sortBy;
    }
    revenueSharingApi
      .fetchRevenueSharingExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = [
          t('sNo'),
          t('date'),
          t('stationName'),
          t('stationId'),
          `${t('totalAmount')} (${branding.text.currency})`,
          `${t('totalPowerConsumed')} (Kwh)`,
          t('facilityOwnerRevenue'),
          t('superAdminRevenue'),
        ];
        const rows = response.data.map((row: StationRevenueSharingInterFace, index: number) => [
          index + 1,
          row.date ? ConvertUTCtoDate(row.date) : '',
          row.chargingStationName,
          row.groupBy,
          row.totalRevenue
            ? `${ToFixedValue(row.totalRevenue)} ${branding.text.currency}`
            : `0.00 ${branding.text.currency}`,
          row.totalMeterDifference ? ToFixedValue(row.totalMeterDifference) : '0.00',
          row.totalFoRevenue
            ? `${ToFixedValue(row.totalFoRevenue)} ${branding.text.currency}`
            : `0.00 ${branding.text.currency}`,
          row.totalAdminRevenue ? ToFixedValue(row.totalAdminRevenue) : '0.00',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'revenue-sharing-list.csv');
          } else {
            exportPDF(headers, rows, 'revenue-sharing-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchRevenueSharing({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchRevenueSharing({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || revenueList.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchRevenueSharing({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableStationRevenueSharingApplied());
    }
  }, [filters]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('revenueSharing'),
        icon: <RevenueIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="table-content">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <CustomTable
        header={column}
        version="v2"
        data={revenueList}
        serialNo
        sizeChangeButtonRequired
        page={page}
        limit={limit}
        loading={loading}
        height={window.innerHeight >= 700 ? 337 : 342}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateStationRevenueSharingFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchStationRevenueSharingFilters}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        rightTopContent={
          <RevenueManagementToggle currentPage={currentPage} setCurrentPage={setCurrentPage} />
        }
        refreshRequired
        handleRefresh={handleRefresh}
        ReferenceCurrentPages={handleRefresh}
        hoverLabel="Refresh"
      />
    </div>
  );
}

export default StationRevenueSharing;
